import { useCallback, useEffect, useState } from 'react';
import { createWebSocketConnection } from '../utils/webrtcConfig';

export default function useInviteListener(userId) {
  const [inviteInfo, setInviteInfo] = useState(null);

  const dismissInvite = useCallback(() => setInviteInfo(null), []);

  const handleInvitePayload = useCallback((raw) => {
    if (!userId) return;
    let payload = raw;
    if (typeof payload === 'string') {
      try { payload = JSON.parse(payload); } catch { return; }
    }
    if (!payload || !payload.joinUrl) return;
    if (payload.outpatientUserId && String(payload.outpatientUserId) !== String(userId)) return;
    setInviteInfo(prev => {
      const incomingRoom = payload.roomId || '';
      if (prev && prev.roomId === incomingRoom) return prev;
      return {
        joinUrl: payload.joinUrl,
        roomId: incomingRoom,
        fromUser: payload.fromUser || '',
        receivedAt: Date.now()
      };
    });
    try { localStorage.removeItem('rtc_invite'); } catch { }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setInviteInfo(null);
      return;
    }
    let bc;
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      try {
        bc = new BroadcastChannel('rtc_invites');
        bc.onmessage = (event) => handleInvitePayload(event.data);
      } catch {
        bc = null;
      }
    }
    const storageHandler = (event) => {
      if (event.key === 'rtc_invite' && event.newValue) {
        handleInvitePayload(event.newValue);
      }
    };
    window.addEventListener('storage', storageHandler);
    try {
      const existing = localStorage.getItem('rtc_invite');
      if (existing) handleInvitePayload(existing);
    } catch { }
    return () => {
      if (bc) {
        try { bc.close(); } catch { }
      }
      window.removeEventListener('storage', storageHandler);
    };
  }, [userId, handleInvitePayload]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let userWs = null;
    let reconnectTimer = null;

    const connect = async () => {
      try {
        userWs = await createWebSocketConnection(`user/${userId}`);
        if (cancelled) {
          try { userWs.close(); } catch { }
          return;
        }
        userWs.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data && data.type === 'invite') {
              handleInvitePayload(data);
            }
          } catch (err) {
            console.error('Failed to parse invite WS message', err);
          }
        };
        userWs.onclose = () => {
          if (!cancelled) {
            reconnectTimer = setTimeout(connect, 2000);
          }
        };
        userWs.onerror = () => {
          if (!cancelled) {
            reconnectTimer = setTimeout(connect, 2000);
          }
        };
      } catch (err) {
        console.error('Failed to connect invite websocket', err);
        if (!cancelled) {
          reconnectTimer = setTimeout(connect, 2000);
        }
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { userWs && userWs.close(); } catch { }
    };
  }, [userId, handleInvitePayload]);

  return { inviteInfo, dismissInvite };
}
